import { useState, useEffect } from "react";
import { FormModal, Field, Input, Textarea, Select } from "./form";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { TASK_TYPES, TASK_PRIORITIES, PIPELINE_STAGES, fullName } from "../lib/constants";

function useContacts(open) {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    if (open) api.get("/contacts?limit=500").then(({ data }) => setContacts(data.items)).catch(() => {});
  }, [open]);
  return contacts;
}

function ContactSelect({ value, onChange, contacts, testid }) {
  return (
    <Select
      testid={testid}
      options={contacts.map((c) => ({ value: c.id, label: `${fullName(c)}${c.city ? " · " + c.city : ""}` }))}
      value={value} onChange={onChange} placeholder="Select contact…"
    />
  );
}

export function TaskModal({ open, onClose, contactId, propertyId, onSaved }) {
  const contacts = useContacts(open && !contactId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ task_type: "Call", priority: "medium", status: "open", due_date: new Date().toISOString().slice(0, 10), contact_id: contactId }); }, [open, contactId]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.post("/tasks", { ...f, property_id: propertyId }); toast.success("Task created"); onSaved?.(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="New Task / Follow-Up" onSubmit={submit} loading={loading} testid="task-modal">
      <Field label="Task" required><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="task-title" /></Field>
      {!contactId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => setF({ ...f, contact_id: v })} testid="task-contact" /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><Select options={TASK_TYPES} value={f.task_type} onChange={(v) => setF({ ...f, task_type: v })} /></Field>
        <Field label="Priority"><Select options={TASK_PRIORITIES} value={f.priority} onChange={(v) => setF({ ...f, priority: v })} /></Field>
        <Field label="Due date"><Input type="date" value={f.due_date || ""} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-date" /></Field>
        <Field label="Due time"><Input type="time" value={f.due_time || ""} onChange={(e) => setF({ ...f, due_time: e.target.value })} /></Field>
      </div>
      <Field label="Notes"><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
    </FormModal>
  );
}

export function NoteModal({ open, onClose, contactId, propertyId, onSaved }) {
  const contacts = useContacts(open && !contactId && !propertyId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ note_type: "general", contact_id: contactId }); }, [open, contactId]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.post("/notes", { ...f, property_id: propertyId }); toast.success("Note added"); onSaved?.(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="Add Note" onSubmit={submit} loading={loading} testid="note-modal">
      {!contactId && !propertyId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => setF({ ...f, contact_id: v })} testid="note-contact" /></Field>}
      <Field label="Note type"><Select options={["general", "prospecting", "call", "meeting", "important"]} value={f.note_type} onChange={(v) => setF({ ...f, note_type: v })} /></Field>
      <Field label="Note" required><Textarea value={f.text || ""} onChange={(e) => setF({ ...f, text: e.target.value })} required data-testid="note-text" className="min-h-[120px]" /></Field>
    </FormModal>
  );
}

export function CallModal({ open, onClose, contactId, contactPhone, onSaved }) {
  const contacts = useContacts(open && !contactId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ direction: "outgoing", outcome: "connected", contact_id: contactId, phone: contactPhone }); }, [open, contactId, contactPhone]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.post("/communications/call", f); toast.success("Call logged"); onSaved?.(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="Log Call" onSubmit={submit} loading={loading} submitLabel="Log Call" testid="call-modal">
      {!contactId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => setF({ ...f, contact_id: v })} testid="call-contact" /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Direction"><Select options={["outgoing", "incoming", "missed"]} value={f.direction} onChange={(v) => setF({ ...f, direction: v })} /></Field>
        <Field label="Outcome"><Select options={["connected", "voicemail", "no_answer", "wrong_number", "interested", "not_interested", "call_back_later", "appointment_set", "other"]} value={f.outcome} onChange={(v) => setF({ ...f, outcome: v })} testid="call-outcome" /></Field>
        <Field label="Duration (sec)"><Input type="number" value={f.duration || ""} onChange={(e) => setF({ ...f, duration: Number(e.target.value) })} /></Field>
        <Field label="Next follow-up"><Input type="date" value={f.next_follow_up || ""} onChange={(e) => setF({ ...f, next_follow_up: e.target.value })} /></Field>
      </div>
      <Field label="Notes"><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="call-notes" /></Field>
    </FormModal>
  );
}

export function SmsModal({ open, onClose, contactId, contactPhone, onSaved, prefill }) {
  const contacts = useContacts(open && !contactId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ contact_id: contactId, to: contactPhone, body: prefill || "" }); }, [open, contactId, contactPhone, prefill]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post("/communications/sms", f); toast.success(data.provider_message || "SMS recorded"); onSaved?.(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="Send SMS" onSubmit={submit} loading={loading} submitLabel="Send SMS" testid="sms-modal">
      {!contactId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => { const c = contacts.find((x) => x.id === v); setF({ ...f, contact_id: v, to: c?.mobile_phone || c?.primary_phone || f.to || "" }); }} testid="sms-contact" /></Field>}
      <Field label="To (phone)"><Input value={f.to || ""} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="+12085551234" data-testid="sms-to" /></Field>
      <Field label="Message" required><Textarea value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} required data-testid="sms-body" /></Field>
      <p className="text-xs text-slate-400">Sends via Twilio when configured in Settings, otherwise recorded to the timeline.</p>
    </FormModal>
  );
}

export function EmailModal({ open, onClose, contactId, contactEmail, onSaved, prefill }) {
  const contacts = useContacts(open && !contactId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ contact_id: contactId, to: contactEmail, subject: prefill?.subject || "", body: prefill?.body || "" }); }, [open, contactId, contactEmail, prefill]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post("/communications/email", f); toast.success(data.provider_message || "Email recorded"); onSaved?.(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="Send Email" onSubmit={submit} loading={loading} submitLabel="Send Email" size="max-w-xl" testid="email-modal">
      {!contactId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => { const c = contacts.find((x) => x.id === v); setF({ ...f, contact_id: v, to: c?.email || f.to || "" }); }} testid="email-contact" /></Field>}
      <Field label="To"><Input type="email" value={f.to || ""} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="email-to" /></Field>
      <Field label="Subject" required><Input value={f.subject || ""} onChange={(e) => setF({ ...f, subject: e.target.value })} required data-testid="email-subject" /></Field>
      <Field label="Body" required><Textarea value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} required className="min-h-[140px]" data-testid="email-body" /></Field>
      <p className="text-xs text-slate-400">Sends via SMTP when configured in Settings, otherwise recorded to the timeline.</p>
    </FormModal>
  );
}

export function OpportunityModal({ open, onClose, contactId, propertyId, onSaved, opportunity }) {
  const contacts = useContacts(open && !contactId);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(opportunity || { stage: "New Lead", role: "buyer", probability: 30, contact_id: contactId, property_id: propertyId }); }, [open, contactId, propertyId, opportunity]);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      if (opportunity) await api.put(`/opportunities/${opportunity.id}`, f);
      else await api.post("/opportunities", f);
      toast.success("Opportunity saved"); onSaved?.(); onClose();
    } catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title={opportunity ? "Edit Opportunity" : "New Opportunity"} onSubmit={submit} loading={loading} testid="opportunity-modal">
      <Field label="Name" required><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="opp-name" /></Field>
      {!contactId && <Field label="Contact"><ContactSelect contacts={contacts} value={f.contact_id} onChange={(v) => setF({ ...f, contact_id: v })} testid="opp-contact" /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role"><Select options={["buyer", "seller", "investor", "landlord", "tenant"]} value={f.role} onChange={(v) => setF({ ...f, role: v })} /></Field>
        <Field label="Stage"><Select options={PIPELINE_STAGES} value={f.stage} onChange={(v) => setF({ ...f, stage: v })} testid="opp-stage" /></Field>
        <Field label="Estimated value"><Input type="number" value={f.estimated_value || ""} onChange={(e) => setF({ ...f, estimated_value: Number(e.target.value) })} data-testid="opp-value" /></Field>
        <Field label="Expected commission"><Input type="number" value={f.expected_commission || ""} onChange={(e) => setF({ ...f, expected_commission: Number(e.target.value) })} /></Field>
        <Field label="Probability %"><Input type="number" value={f.probability || ""} onChange={(e) => setF({ ...f, probability: Number(e.target.value) })} /></Field>
        <Field label="Expected close"><Input type="date" value={f.expected_close_date || ""} onChange={(e) => setF({ ...f, expected_close_date: e.target.value })} /></Field>
      </div>
    </FormModal>
  );
}
